import React, { useState, useEffect } from 'react';
import './PrivacyPolicy.css';
import Footer from './Footer';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const PrivacyPolicy = () => {
  const location = useLocation(); // Get location 
  const [activeLink, setActiveLink] = useState('');

  // This effect runs once when the component mounts
  useEffect(() => {
    // Get the current path from the URL (e.g., "/privacy-policy")
    const currentPath = window.location.pathname;
    setActiveLink(currentPath);
  }, []); // The empty array ensures this effect runs only once

  return (
    <>
      <Helmet>
        <meta name="title" content="Privacy Policy | Get Next Visa - AI Powered Student Visa Report" />
        <title>Privacy Policy | Get Next Visa - AI Powered Student Visa Report</title>
        <meta name="description" content="Get personalized, AI-powered visa reports. Discover the best countries and colleges for your study abroad journey." />
      </Helmet>

      <div className="privacy-banner">
        <div className="privacy-container">
          <nav className="privacy-nav">
            <ul>
              {/* Conditionally apply the 'active' class based on the current path */}
              <li><a href="/privacy-policy" className={activeLink === '/privacy-policy' ? 'active' : ''}>PRIVACY POLICY</a></li>
              <li><a href="/terms" className={activeLink === '/terms' ? 'active' : ''}>TERMS OF SERVICE</a></li>
              <li><a href="/refund-policy" className={activeLink === '/refund-policy' ? 'active' : ''}>REFUND POLICY</a></li>
            </ul>
          </nav>
          <main className="privacy-content">
            <header className="privacy-header">
              <h1>Privacy Policy for Get Next Visa</h1>
              <p><b>Last updated: July 16, 2025 </b></p>
              <p><b>Thank you for choosing GetNextVisa (“we,” “us,” or “our”). We respect your privacy and are
                committed to protecting your personal information. This Privacy Policy explains how we
                collect, use, disclose, and safeguard the information you provide when you
                visit https://getnextvisa.com (the “Website”) and purchase our AI‑driven educational visa
                reports (“Services”). By accessing or using our Website and Services, you consent to this
                Privacy Policy.</b> </p>
            </header>


            <section className="privacy-section">
              <h2>1. Information We Collect</h2>


              <ul>

                <p>When you register or purchase Services, we collect:</p>

              </ul>

              <h2>1.1 Personal Information </h2>
              <ul>
                <li><b>Identity Data:</b> Full name, date of birth, gender </li>

                <li><b>Contact Data:</b> Email address, phone number, mailing address </li>

                <li><b>Academic & Financial Data:</b> Educational background, test scores (e.g.,
                  IELTS/PTE), family income, bank balance </li>

                <li><b>Family Details:</b> Parents’ education and income, and other details you voluntarily
                  provide</li>
                <li><b>Payment Data:</b> Billing address and payment method details (processed via secure
                  third‑party gateways)</li>

              </ul>


              <h2>1.2 Technical & Usage Data </h2>
              <ul>
                <li><b>Device & Browser Info:</b> IP address, browser type/version, operating system, device identifiers</li>
                <li><b>Usage Data:</b> Pages visited, timestamps, referring URLs, interactions with the Website</li>

              </ul>


              <h2>1.3 Cookies & Tracking </h2>
              <p>We use cookies and similar technologies to personalize your experience, remember
                preferences, and analyze traffic. See Section 12 for full cookie categories.</p>
            </section>

            <section className="privacy-section">
              <h2>2. How We Use Your Information</h2>
              <ul>
                <li><b>Deliver Services:</b> Generate and deliver personalized AI–driven education visa reports</li>
                <li><b>Process Payments:</b> Complete transactions and issue receipts</li>
                <li><b>Communicate:</b> Respond to inquiries, send confirmations, updates, and (with your consent) marketing communications</li>
                <li><b>Improve Our Website:</b> Analyze behavior to troubleshoot, optimize functionality, and enhance user experience</li>
                <li><b>Ensure Security & Compliance:</b> Detect fraud, enforce our Terms of Service, and meet legal obligations</li>
              </ul>
            </section>




            <section className="privacy-section">
              <h2>3. Consent & Policy Acceptance</h2>
              <p>By providing your personal information and using our Services, you consent to the collection,
                use, and disclosure of your data as described herein. You may withdraw consent at any time
                by closing your account or contacting us (see Section 18).</p>
            </section>


            <section className="privacy-section">
              <h2>4. Disclosure of Your Information?</h2>
              <ul>
                <li><b>Service Providers:</b> AI/ML platforms, hosting providers, payment processors (e.g.,
                  Razorpay), analytics services—only to perform Services on our behalf under
                  confidentiality obligations </li>
                <li><b> Legal & Safety:</b> Law enforcement or regulatory bodies when required by law or to
                  protect our rights, privacy, safety, or property</li>
                <li> <b>Business Transfers: </b>In the event of a merger, acquisition, or sale of assets, your
                  data may be transferred as part of the transaction, subject to confidentiality
                  commitments</li>
              </ul>
            </section>



            <section className="privacy-section">
              <h2>5. Third‑Party Links & Embedded Content</h2>
              <ul>
                <li><b>Chat/WhatsApp Widget:</b> Our in‑house chat system captures user phone number,
                  email, and other basic data you enter into the chat interface. </li>

                <li><b>Embedded Videos & Maps:</b> We display YouTube videos and Google Maps on our
                  site; these embeds do not collect personal data from users through our integration,
                  but you may wish to review YouTube’s Privacy Policy and Google Maps’ Privacy
                  Policy for how those services handle data.
                </li>
                <li><b>External Links:</b> If you click links to external sites, we are not responsible for their
                  privacy practices. Please review their policies before sharing personal information.
                </li>
              </ul>
            </section>


            <section className="privacy-section">
              <h2>6. Analytics & Tracking Tools</h2>
              {/* <p>We use performance and marketing analytics to understand and improve our Website:</p> */}

              <ul>
                <li><b>Google Analytics:</b> Tracks aggregate usage patterns. You can opt out via Google’s
                  browser add‑on.</li>
                <li><b>Meta Pixel (Facebook):</b>  Tracks conversions and engagement. You can manage
                  ad‑tracking preferences in your Facebook settings.</li>
                <p>We honor browser “Do Not Track” signals where technically feasible (see Section 13).</p>
              </ul>


            </section>


            <section className="privacy-section">
              <h2>7. Newsletter & Marketing Opt‑Ins</h2>

              <ul>
                <li>If you subscribe to our email newsletter or marketing communications, we may send:</li>

                <li>Product updates, new features, and educational content</li>
                <li>Occasional announcements of promotions or events</li>
                <p>Each email includes an embedded unsubscribe button. You may opt out at any time, and we
                  will promptly remove you from the mailing list.
                </p>
              </ul>

            </section>
            <section className="privacy-section">
              <h2>8. Payment Compliance</h2>
              <p>We process payments exclusively through Razorpay, which maintains PCI‑DSS compliance.
                We do not store your raw card details on our servers—only Razorpay’s secure tokens.</p>
            </section>


            <section className="privacy-section">
              <h2>9. User‑Generated Content</h2>

              <ul>
                <p>If you post comments, reviews, or upload files on our Website:
                </p>
                <li>We retain that content indefinitely unless you request deletion</li>

                <li>We moderate submissions to remove unlawful or inappropriate contentt</li>
                <li>We may share aggregated or anonymized feedback for marketing or improvement
                  purposes, but never publish personal identifiers without your consent</li>
              </ul>
            </section>



            <section className="privacy-section">
              <h2>10. Data Security</h2>

              <ul>
                <p>We implement reasonable administrative, technical, and physical safeguards to protect your
                  data, including:
                </p>
                <li><b>Encryption:</b> TLS/SSL for data in transit</li>

                <li><b>Secure Storage & Access Controls</b></li>
                <li><b>Regular Security Assessments & Patch Management</b></li>
                <p>However, no Internet transmission or electronic storage is 100% secure. We cannot
                  guarantee absolute security</p>
              </ul>
            </section>


            <section className="privacy-section">
              <h2>11. Data Retention & Deletion Requests</h2>

              <ul>

                <li><b> Active Records:</b> We retain personal and financial data as long as your account is
                  active or as needed to provide Services</li>

                <li><b>Legal Compliance:</b>  We may retain information to comply with legal obligations,
                  resolve disputes, and enforce agreements</li>
                <li><b>Regular Security Assessments & Patch Management</b></li>
                <li><b>Deletion Requests:</b> You may request deletion of your personal data at any time by
                  contacting us (see Section 18). We will honor such requests subject to legal
                  exceptions.</li>
              </ul>
            </section>

            <section className="privacy-section">
              <h2>12. Cookies & Tracking Policy</h2>

              <ul>
                <p>We use the following cookie categories:</p>

                <li><b>Strictly Necessary:</b> Essential for website functionality</li>
                <li><b>Performance:</b> Aggregate analytics (e.g., Google Analytics)</li>
                <li><b>Functional:</b> Remember preferences like language and display settings</li>
                <li><b>Advertising:</b> Serve relevant ads via Meta Pixel and other partners</li>
                <p>Manage or disable cookies via your browser settings. Disabling strictly necessary cookies
                  may impair key features.
                </p>
              </ul>

            </section>



            <section className="privacy-section">
              <h2>13. “Do Not Track” Signals</h2>

              <p>We honor browser “Do Not Track” (DNT) headers. If your browser sends a DNT signal, we
                will refrain from tracking your browsing activity where technically feasible.</p>

            </section>

            <section className="privacy-section">
              <h2>14. Automated Decision‑Making & Profiling</h2>

              <p>We generate AI‑based college and visa recommendations, but no part of our Service makes
                binding automated decisions. All reports are strictly advisory</p>

            </section>


            <section className="privacy-section">
              <h2>15. Data Residency & International Transfers
              </h2>

              <p>All of our servers and primary service providers are located in India. We do not transfer
                personal data outside India at this time.
              </p>

            </section>



            <section className="privacy-section">
              <h2> 16. Global Compliance</h2>

              <ul>
                <p>We comply with applicable global data protection laws, including :</p>

                <li><b>Strictly Necessary:</b> Essential for website functionality</li>
                <li><b>GDPR</b> (for European residents)</li>
                <li><b>CCPA/CPRA</b> (for California residents)</li>
                <li><b>Indian data‑protection regulations</b></li>
                <li><b>Australian Privacy Principles</b></li>
                <p>Where additional rights or disclosures are required under these laws, we will honor them. </p>
              </ul>

            </section>

            <section className="privacy-section">
              <h2>17. Breach Notification
              </h2>

              <p>In the unlikely event of a data breach, we will notify affected users and relevant authorities
                <b>within 24 hours</b> of discovery, including details of the breach and recommended user
                actions.
              </p>

            </section>


            <section className="privacy-section">
              <h2> 18. Policy Updates & Notifications</h2>

              <ul>
                <p>We may update this Privacy Policy to reflect changes in our practices or legal obligations.
                  When we make material changes:</p>

                <li>We will post the updated policy on the Website with a new “Last updated” date

                </li>
                <li>We will email all registered users notifying them of the change</li>
              </ul>

            </section>


            <section className="privacy-section">
              <h2>19. Future Cookie‑Consent Banner</h2>
              <p>We plan to implement a cookie‑consent banner in the near future. When live, it will allow you
                to grant or withdraw consent for non‑essential cookies at any time.</p>
            </section>


            <section className="privacy-section">
              <h2>20. Contact Us  </h2>

              <ul>
                <p>If you have questions, wish to withdraw consent, or exercise any privacy rights, you may
                  contact us via:</p>
                <li>Email: <a href="mailto:privacy@gnvisa.com">privacy@getnextvisa.com</a></li>
                <li>Support Ticket: <a href="/Contact">https://getnextvisa.com/Contact</a></li>
              </ul>

            </section>

            <p>Thank you for trusting GetNextVisa with your personal information and educational journey.
              Your privacy is our priority.</p>
          </main>
        </div>

      </div>

      <Footer />
    </>
  );
};

export default PrivacyPolicy;